import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Banknote, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `R ${(parseFloat(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const METHOD_COLORS = {
  CASH: "bg-green-500", CARD: "bg-blue-500", ACCOUNT: "bg-orange-500", EFT: "bg-purple-500",
};
const colorFor = (m) => METHOD_COLORS[m] || "bg-gray-400";
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

const MixBar = ({ row, methods }) => (
  <div className="flex h-2.5 w-full min-w-[120px] overflow-hidden rounded-full bg-gray-100">
    {methods.map((m) => {
      const share = row.methods?.[m]?.share || 0;
      return share > 0 ? <div key={m} className={colorFor(m)} style={{ width: `${share}%` }} title={`${m} ${share}%`} /> : null;
    })}
  </div>
);

const Legend = ({ methods }) => (
  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
    {methods.map((m) => (
      <span key={m} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${colorFor(m)}`} />{m}</span>
    ))}
  </div>
);

const MixTable = ({ rows, methods, mode, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-100 text-left">
          <th className="px-3 py-2 font-medium">{mode === "product" ? "Product" : "Category"}</th>
          <th className="px-3 py-2 font-medium text-right">Total</th>
          <th className="px-3 py-2 font-medium text-right">Lines</th>
          {methods.map((m) => <th key={m} className="px-3 py-2 font-medium text-right">{m}</th>)}
          <th className="px-3 py-2 font-medium">Mix</th>
          <th className="px-3 py-2 font-medium">Leans To</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b hover:bg-gray-50" data-testid={`payment-mix-row-${slug(r.key)}`}>
            <td className="px-3 py-2">
              <div className="font-medium">{mode === "product" ? r.name : r.category}</div>
              {mode === "product" && <div className="text-xs text-gray-500">{r.plu_code} · {r.category}</div>}
            </td>
            <td className="px-3 py-2 text-right font-semibold">{fmt(r.value)}</td>
            <td className="px-3 py-2 text-right">{r.txn_count}</td>
            {methods.map((m) => (
              <td key={m} className="px-3 py-2 text-right whitespace-nowrap">
                <div>{fmt(r.methods?.[m]?.value)}</div>
                <div className="text-xs text-gray-500">{r.methods?.[m]?.share || 0}%</div>
              </td>
            ))}
            <td className="px-3 py-2"><MixBar row={r} methods={methods} /></td>
            <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs font-semibold text-white ${colorFor(r.leaning)}`}>{r.leaning}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
    {rows.length === 0 && <div className="py-8 text-center text-gray-500">No sales in this period</div>}
  </div>
);

const LeanList = ({ title, icon: Icon, rows, method, avg, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center gap-2 text-sm"><Icon className="h-4 w-4" />{title}</CardTitle>
      <div className="text-xs text-gray-500">Store average {method} share: {avg}%</div>
    </CardHeader>
    <CardContent className="space-y-1.5">
      {rows.length === 0 && <div className="text-sm text-gray-500">Not enough sales yet</div>}
      {rows.map((r, i) => (
        <div key={r.key} className="flex items-center justify-between gap-2 text-sm border-b pb-1">
          <span className="truncate"><span className="text-gray-400 mr-1">{i + 1}.</span>{r.name}</span>
          <span className="whitespace-nowrap font-semibold">{r.methods?.[method]?.share}% <span className="text-xs font-normal text-gray-500">of {fmt(r.value)}</span></span>
        </div>
      ))}
    </CardContent>
  </Card>
);

export const PaymentMixReport = ({ data, startDate, endDate, minTxns, onMinTxnsChange }) => {
  const [mode, setMode] = useState("category");
  const [minInput, setMinInput] = useState(minTxns);
  const [exporting, setExporting] = useState(false);
  const methods = data.methods || [];
  const overall = data.overall || { methods: {} };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/sales/payment-mix/export`, {
        params: { start_date: startDate, end_date: endDate, min_txns: minTxns }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payment_mix_${startDate}_to_${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export Payment Mix");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-mix-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Payment Mix by Product: {data.period?.start} to {data.period?.end}</h3>
        <Button onClick={handleExport} disabled={exporting} className="bg-green-700 hover:bg-green-800 text-white" data-testid="payment-mix-export-btn">
          {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Export Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payment-mix-summary">
        {methods.map((m) => (
          <div key={m} className="rounded-lg border p-3" data-testid={`payment-mix-summary-${slug(m)}`}>
            <div className="flex items-center gap-2 text-xs font-medium uppercase text-gray-600"><span className={`h-2.5 w-2.5 rounded-full ${colorFor(m)}`} />{m}</div>
            <div className="text-xl font-bold">{fmt(overall.methods[m]?.value)}</div>
            <div className="text-xs text-gray-500">{overall.methods[m]?.share}% of item sales</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <LeanList title="Top 20 Card-Leaning Products" icon={CreditCard} rows={data.card_leaning || []} method="CARD" avg={overall.methods.CARD?.share || 0} testId="payment-mix-card-leaning" />
        <LeanList title="Top 20 Cash-Leaning Products" icon={Banknote} rows={data.cash_leaning || []} method="CASH" avg={overall.methods.CASH?.share || 0} testId="payment-mix-cash-leaning" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-md border p-0.5" data-testid="payment-mix-mode-toggle">
              {["category", "product"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`payment-mix-mode-${m}`}
                  className={`rounded px-3 py-1 text-sm font-medium transition-colors ${mode === m ? "bg-purple-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}>
                  By {m === "category" ? "Category" : "Product"}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs">Min. sale lines for Top 20 lists</Label>
                <Input type="number" min={1} value={minInput} onChange={(e) => setMinInput(e.target.value)} className="h-8 w-24" data-testid="payment-mix-min-txns-input" />
              </div>
              <Button size="sm" variant="outline" onClick={() => onMinTxnsChange(Math.max(1, parseInt(minInput, 10) || 1))} data-testid="payment-mix-apply-min-btn">Apply</Button>
            </div>
          </div>
          <Legend methods={methods} />
          <div className="text-xs text-gray-500">Split payments are shared out across the items in the sale by value. Cash change is excluded. Refunds, petty cash and self-use are left out.</div>
        </CardHeader>
        <CardContent>
          <MixTable rows={mode === "category" ? data.by_category || [] : data.by_product || []} methods={methods} mode={mode} testId={`payment-mix-table-${mode}`} />
        </CardContent>
      </Card>
    </div>
  );
};
